<template>
  <v-layout column fill-height>
    <v-list class="pb-0" style="border-bottom:1px solid #dddddd" dense>
      <v-list-item class="pl-2">
        <v-list-item-action class="mr-2 my-0">
          <v-btn
            :disabled="value.length === 0 && doubleClickTimer === null"
            title="Auswahl zurücksetzen. Doppelklick um zusätzlich die Suche zurückzusetzen"
            icon
            @click="clearOptions"
          >
            <v-badge
              :content="value.length"
              :value="value.length"
              color="red"
              overlap
            >
            <v-icon>mdi-close-circle</v-icon>
            </v-badge>
          </v-btn>
            </v-list-item-action>

          <v-list-item-content class="py-0 mb-n3">
            <v-text-field
            v-model="search"
            placeholder="Suche..."
            class="py-0"
            clearable
            @focus="$event.target.select()"
          />
        </v-list-item-content>
      </v-list-item>

      <v-expand-transition>
        <div v-show="showQuickSumOptions">
          <v-list-item>
            <v-list-item-action class="mr-2 my-0">
              <v-checkbox v-model="filterQuickSumsBySelection"/>
            </v-list-item-action>
            <v-list-item-content>
              Summen auf Auswahl beschränken
            </v-list-item-content>
          </v-list-item>

          <v-list-item title="Summen aufsteigend sortieren">
            <v-list-item-action class="mr-2 my-0">
              <v-checkbox v-model="orderByQuickSum"/>
            </v-list-item-action>
            <v-list-item-content>
              Summen absteigend sortieren
            </v-list-item-content>
          </v-list-item>
        </div>
        </v-expand-transition>
        <v-btn icon block x-small class="rounded-0" @click="showQuickSumOptions = !showQuickSumOptions">
          <v-icon v-if="showQuickSumOptions">mdi-chevron-up</v-icon>
          <v-icon v-else> mdi-chevron-down</v-icon>
        </v-btn>
      </v-list>

      <div>
        <v-progress-linear
	  :active="loading || quickSumsLoading"
	  indeterminate
        />
      </div>

    <v-flex class="scrolling">
      <v-list v-if="!loading && options.length > 0" dense>
        <v-list-item
          v-for="option in filteredOptions"
          :key="option.id"
          :title="getLabel(option)"
          @click="toggleOption(option)"
        >
          <v-list-item-action class="mr-2 my-0">
            <v-checkbox
              :input-value="isOptionSelected(option)"
              @click.prevent.stop=""
              @change="toggleOption(option)"
            />
          </v-list-item-action>
          <slot :option="option">
            <v-list-item-content class="py-1">
                {{ getLabel(option) }}
            </v-list-item-content>
             <v-list-item-action>
               <v-list-item-action-text>
                 {{ formatHour(option.taskduration_sum)}}
               </v-list-item-action-text>
             </v-list-item-action>
          </slot>
        </v-list-item>
      </v-list>
      <div v-else-if="!loading && options.length === 0">
        <slot name="noData">
          <p class="text-xs-center">
            Keine Daten.
          </p>
        </slot>
      </div>
    </v-flex>
  </v-layout>
</template>

<script>
import { mapGetters } from 'vuex'
import _ from 'lodash'

import {formatNumberDuration, formatStringDuration} from './../filters/Duration'

export default {
  name: 'ListSelect',
  props: {
    value: {
      type: Array,
      default: () => [],
    },
    options: {
      type: Array,
      default: () => [],
    },
    label: {
      type: String,
      default: "label",
    },
    customLabel: {
      type: Function,
      default: null
    },
    trackBy: {
      type: String,
      default: "id",
    },
    loading: {
      type: Boolean,
      default: false,
    },
    quickSums: {
      type: Object,
      default: () => {},
    },
    quickSumsLoading: {
      type: Boolean,
      default: false,
    },
    orderBy: {
      type: String,
      default: "name"
    },
    filterQuickSumsInput: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      search: "",
      showQuickSumOptions: false,
      filterQuickSumsBySelection: false,
      orderByQuickSum: false,
      doubleClickDelay: 500,
      doubleClickCount: 0,
      doubleClickTimer: null,
    }
  },
  watch: {
    filterQuickSumsBySelection: {
      handler () {
        this.$emit("filterQuickSumsBySelectionChange", this.filterQuickSumsBySelection)
      },
      immediate: true
    },
    filterQuickSumsInput: {
      handler (value) {
        this.filterQuickSumsBySelection = value
      },
      immediate: true
    }
  },
  computed: {
    filteredOptions () {
      let filtered = this.options.filter(this.matchesSearch)
      filtered = filtered.map(item => {
        return  {...item, ...{taskduration_sum: this.quickSums[item.id] || 0}}
      })
      if (this.orderByQuickSum) {
        return _.orderBy(filtered, "taskduration_sum", "desc")
      }
      return filtered
    },
    ...mapGetters(['profile'])
  },
  methods: {
    track (option) {
      return option[this.trackBy]
    },
    isOptionSelected (option) {
      return this.value.some(item => this.track(item) === this.track(option))
    },
    toggleOption (option) {
      if (this.isOptionSelected(option)) {
        this.deselectOption(option)
      } else {
        this.selectOption(option)
      }
    },
    selectOption (option) {
      this.$emit("input", [...this.value, option])
    },
    deselectOption (option) {
      this.$emit("input", this.value.filter(item => this.track(item) !== this.track(option)))
    },
    clearOptions() {
      if (this.value !== []){
        this.$emit("input", [])
      }

      this.doubleClickCount++
      if (this.doubleClickCount === 1) {
        this.doubleClickTimer = setTimeout( () => {
          this.doubleClickCount = 0
          this.doubleClickTimer = null
        }, this.doubleClickDelay)
      } else {
         clearTimeout(this.doubleClickTimer);
         this.doubleClickTimer = null
         this.doubleClickCount = 0
         this.search = ""
      }
    },
    getLabel (option) {
      if (this.customLabel) {
        return this.customLabel(option)
      }
      return option[this.label]
    },
    matchesSearch (option) {
      return this.search === null || this.search.length === 0 || this.getLabel(option).toLowerCase().includes(this.search.toLowerCase())
    },
    formatHour (value) {
      return this.profile.format_hour_as_number ? formatNumberDuration(value) : formatStringDuration(value)
    },
    setFilterQuickSumsBySelection (value) {
      this.filterQuickSumsBySelection = value
    }
  }
}
</script>

<style lang="scss">
.scrolling {
  overflow-y: auto;
}
</style>
